import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip';

import messages from './messages';
import { Container } from './styled';
import { type CopyToClipboardProps } from './types';

export const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
	value,
	tooltip = messages.defaultCopyMessage,
	children,
}) => {
	const [isCopied, setCopied] = useState(false);
	const updateTooltip = useRef<() => void>();
	const { formatMessage } = useIntl();

	const content = useMemo(() => {
		return formatMessage(isCopied ? messages.copiedMessage : tooltip);
	}, [formatMessage, isCopied, tooltip]);

	// recalculate tooltip position on content change
	// https://atlassian.design/components/tooltip/examples#updating-tooltip-position
	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [content]);

	const copy = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			const clipboard = window.navigator.clipboard;
			await clipboard.writeText(value);
			setCopied(true);
		},
		[value],
	);

	useEffect(() => {
		if (isCopied) {
			const timeout = setTimeout(() => {
				setCopied(false);
			}, 5000);

			return () => clearTimeout(timeout);
		}
	}, [isCopied]);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<Container onClick={copy} className="copy-to-clipboard">
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;
					return content;
				}}
			>
				{children({ isCopied })}
			</Tooltip>
		</Container>
	);
};
